import React, { useState, useEffect } from 'react';
import { useSiteData } from '@/context/SiteDataContext';

const SearchSection = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const { destinations } = useSiteData();

  // Get dates for autofill
  const getDefaultDates = () => {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() + 7); // 1 week from now
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 5); // 5 days trip
    
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  const defaultDates = getDefaultDates();

  const [searchData, setSearchData] = useState({
    from: 'Mumbai',
    to: '',
    startDate: defaultDates.startDate,
    endDate: defaultDates.endDate,
    adults: '2',
    children: '0'
  });

  const today = new Date().toISOString().split('T')[0];

  // Set first destination as default once loaded
  useEffect(() => {
    if (destinations.length > 0) {
      setSearchData(prev => (prev.to ? prev : { ...prev, to: destinations[0].name }));
    }
  }, [destinations]);

  // Validation rules
  const validateField = (name, value) => {
    switch (name) {
      case 'from':
        if (!value.trim()) return 'Starting city is required';
        if (value.trim().length < 2) return 'Enter a valid city name';
        if (value.trim().toLowerCase() === searchData.to.trim().toLowerCase()) 
          return 'From and To cannot be the same';
        return '';
      case 'to':
        if (!value) return 'Please select a destination';
        if (value.trim().toLowerCase() === searchData.from.trim().toLowerCase()) 
          return 'From and To cannot be the same';
        return '';
      case 'startDate':
        if (!value) return 'Departure date is required';
        if (value < today) return 'Departure date cannot be in the past';
        return '';
      case 'endDate':
        if (!value) return 'Return date is required';
        if (value < searchData.startDate) return 'Return date must be after departure';
        if (value === searchData.startDate) return 'Trip must be at least 1 day';
        return '';
      case 'adults':
        if (!value || parseInt(value) < 1) return 'At least 1 adult is required';
        return '';
      default:
        return '';
    }
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};
    Object.keys(searchData).forEach(key => {
      const error = validateField(key, searchData[key]);
      if (error) newErrors[key] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allTouched = {};
    Object.keys(searchData).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    setTimeout(() => {
      setIsLoading(false);
      
      // Scroll to Top Destinations section
      const destinationsSection = document.querySelector('#destinations');
      if (destinationsSection) {
        destinationsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 400);
  };

  return (
    <section className="search-section-wrapper">
      <main className="search-main-container">
        {/* Search Component */}
        <div className="search-card-new" data-testid="search-card">
          {/* Section Title */}
          <h2 className="search-section-title">Search</h2>
          
          <form onSubmit={handleSearch} className="search-form-new" data-testid="search-form" noValidate>
            {/* Row 1: Origins */}
            <div className="search-row-origins">
              <div className="search-field-group">
                <label className="search-label">From <span className="required-asterisk">*</span></label>
                <div className="search-input-wrapper">
                  <i className="fa-solid fa-plane-departure search-icon"></i>
                  <input 
                    type="text" 
                    name="from"
                    value={searchData.from}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Starting City" 
                    className={`search-input-new ${touched.from && errors.from ? 'input-error' : ''}`}
                    data-testid="search-from-input"
                  />
                </div>
                {touched.from && errors.from && (
                  <span className="field-error-message" data-testid="from-error">{errors.from}</span>
                )}
              </div>
              <div className="search-field-group">
                <label className="search-label">To <span className="required-asterisk">*</span></label>
                <select 
                  name="to"
                  value={searchData.to}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`search-select-new ${touched.to && errors.to ? 'input-error' : ''}`}
                  data-testid="search-to-select"
                >
                  <option value="">Select Destination</option>
                  {destinations.map(dest => (
                    <option key={dest.id} value={dest.name}>
                      {dest.name}
                    </option>
                  ))}
                </select>
                {touched.to && errors.to && (
                  <span className="field-error-message" data-testid="to-error">{errors.to}</span>
                )}
              </div>
            </div>

            {/* Row 2: Dates & Guests */}
            <div className="search-row-details">
              <div className="search-field-group">
                <label className="search-label">Departure <span className="required-asterisk">*</span></label>
                <div className="search-input-wrapper">
                  <i className="fa-solid fa-calendar-day search-icon"></i>
                  <input 
                    type="date" 
                    name="startDate"
                    value={searchData.startDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    min={today}
                    className={`search-input-new ${touched.startDate && errors.startDate ? 'input-error' : ''}`}
                    data-testid="search-departure-date"
                  />
                </div>
                {touched.startDate && errors.startDate && (
                  <span className="field-error-message" data-testid="startDate-error">{errors.startDate}</span>
                )}
              </div>
              <div className="search-field-group">
                <label className="search-label">Return <span className="required-asterisk">*</span></label>
                <div className="search-input-wrapper">
                  <i className="fa-solid fa-calendar-check search-icon"></i>
                  <input 
                    type="date" 
                    name="endDate"
                    value={searchData.endDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    min={searchData.startDate || today}
                    className={`search-input-new ${touched.endDate && errors.endDate ? 'input-error' : ''}`}
                    data-testid="search-return-date"
                  />
                </div>
                {touched.endDate && errors.endDate && (
                  <span className="field-error-message" data-testid="endDate-error">{errors.endDate}</span>
                )}
              </div>
              <div className="search-field-group">
                <label className="search-label">Adults <span className="required-asterisk">*</span></label>
                <select 
                  name="adults"
                  value={searchData.adults}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`search-select-new ${touched.adults && errors.adults ? 'input-error' : ''}`}
                  data-testid="search-adults-select"
                >
                  <option value="1">1 Adult</option>
                  <option value="2">2 Adults</option>
                  <option value="3">3 Adults</option>
                  <option value="4">4 Adults</option>
                  <option value="5">5 Adults</option>
                </select>
                {touched.adults && errors.adults && (
                  <span className="field-error-message" data-testid="adults-error">{errors.adults}</span>
                )}
              </div>
              <div className="search-field-group">
                <label className="search-label">Children</label>
                <select 
                  name="children"
                  value={searchData.children}
                  onChange={handleChange}
                  className="search-select-new"
                  data-testid="search-children-select"
                >
                  <option value="0">0 Children</option>
                  <option value="1">1 Child</option>
                  <option value="2">2 Children</option>
                  <option value="3">3 Children</option>
                  <option value="4">4 Children</option>
                </select>
              </div>
            </div>

            {/* Action */}
            <div className="search-action-row">
              <button 
                type="submit" 
                className="search-submit-btn-new"
                disabled={isLoading}
                data-testid="search-submit-btn"
              >
                <span>{isLoading ? 'Searching...' : 'Search Packages'}</span>
                {isLoading ? (
                  <div className="search-loading-spinner"></div>
                ) : (
                  <i className="fa-solid fa-magnifying-glass"></i>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </section>
  );
};

export default SearchSection;
